import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import { Flag } from '@xingternal/flag';
import { IconMessages } from '@xingternal/icons';
import { ProfileImage } from '@xingternal/profile-image';
import { ProfileInfo, ProfileInfoAlignment } from '@xingternal/profile-info';
import { BodyCopy, Headline, Meta } from '@xingternal/typography';

import { ScrollTrackingModuleWrapper } from '@/components/job-posting/components/scroll-tracking-module-wrapper';
import { JobDetailContext } from '@/contexts';
import { useHrContactClickReasonQuery } from '@/graphql/generated';
import { appendQueryParams } from '@/helpers/query-string';
import { redirectUrl } from '@/helpers/redirect-url';
import { ConnectedContact } from '@/types';

import { ModuleTitle } from '../company-module';

import * as S from './hr-contact.styles';

type HRContactProps = {
  contact: ConnectedContact;
  alignment: ProfileInfoAlignment;
  showTitle?: boolean;
  companyId?: string;
  className?: string;
};

export const HRContact: React.FC<HRContactProps> = ({ contact, alignment, showTitle, companyId = '', className }) => {
  const { user } = contact;
  const { slug: jobPostingSlug } = useContext(JobDetailContext);
  const { displayName, firstName, lastName, connectionDegree, occupations, profileImage, slug: userSlug } = user ?? {};
  const userName = displayName || `${firstName} ${lastName}`;
  const occupation = occupations && occupations[0];

  const { data: updatedClickReasonData } = useHrContactClickReasonQuery({
    variables: { jobPostingSlug, companyId },
    skip: !companyId,
  });

  const buildHRProfileUrlWithTracking = (): string => {
    let url = `/profile/${userSlug}`;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore it complains about the potential UnauthorizedJob type on job and we don´t have the exact query generated job type available
    const updatedClickReason = `${updatedClickReasonData?.job?.contact?.user?.clickReasonProfileUrlFromEntity?.code}`;
    if (updatedClickReason) {
      url += `/${updatedClickReason}`;
    }
    return appendQueryParams(url, {
      sc_o: 'jobs_profile_other_visit_contact_module',
      sc_o_PropActionOrigin: 'jobs_hr_contact',
    });
  };

  return (
    <ScrollTrackingModuleWrapper moduleTrackingName="HRContact" data-testid="hr-contact">
      {showTitle ? <ModuleTitle titleLabel="JOB_DETAILS_HR_TITLE" /> : null}
      <S.StyledHRContainer
        onClick={(event: React.MouseEvent<HTMLElement>) => redirectUrl(event, buildHRProfileUrlWithTracking())}
        className={className}
      >
        <ProfileInfo
          alignment={alignment}
          profileImage={
            <ProfileImage
              connectionDegree={connectionDegree}
              profileName={userName}
              size="medium"
              src={profileImage?.[0]?.url}
              type="male-user"
            />
          }
          actions={
            <S.StyledButton
              size="small"
              variant="secondary"
              icon={IconMessages}
              onClick={(event: React.MouseEvent<HTMLElement>) => event.stopPropagation()}
              to={`/chats/new/${userSlug}?contextId=jobs_contact_hr&entryPoint=jobs_job_detail_recruiter`}
            >
              <FormattedMessage id="JOBS_DETAIL_COMPANY_CONTACT_MESSAGE" />
            </S.StyledButton>
          }
        >
          <Headline size="large">{userName}</Headline>
          <Flag size="medium" variant="premium" />
          {occupation?.subline ? <BodyCopy size="medium">{occupation.subline}</BodyCopy> : null}
          {occupation?.headline ? <Meta size="small">{occupation.headline}</Meta> : null}
        </ProfileInfo>
      </S.StyledHRContainer>
    </ScrollTrackingModuleWrapper>
  );
};
